import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { FiVideo, FiDownload } from 'react-icons/fi';
import './VideoGeneration.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const VideoGeneration = () => {
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState('8');
  const [quality, setQuality] = useState('720p');
  const [generating, setGenerating] = useState(false);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [error, setError] = useState(null);
  const [tokenUsage, setTokenUsage] = useState(null);

  const handleGenerateVideo = async () => {
    // Validation
    if (!prompt.trim()) {
      setError('Please enter a video description');
      return;
    }

    setGenerating(true);
    setError(null);
    setGeneratedVideo(null);

    try {
      const requestBody = { prompt: prompt.trim(), duration: parseInt(duration), quality };

      const response = await fetch(`${API_BASE_URL}/generate-video`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        setGeneratedVideo({
          url: data.videoUrl,
          metadata: data.metadata,
          prompt: prompt
        });
        setTokenUsage(data.tokenUsage);
      } else {
        throw new Error(data.error || 'Video generation failed');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadVideo = () => {
    if (!generatedVideo) return;
    const a = document.createElement('a');
    a.href = generatedVideo.url;
    a.download = `generated-video-${Date.now()}.mp4`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleReset = () => {
    setPrompt('');
    setGeneratedVideo(null);
    setError(null);
    setTokenUsage(null);
  };

  return (
    <div className="video-generation-container">
      <div className="flex relative">
        <Sidebar />
        <main className="flex-1 min-h-screen bg-gray-50">
          <div className="video-generation-layout">

            {/* Header */}
            <div className="page-header">
              <div className="header-content">
                <div className="flex items-center gap-3">
                  <FiVideo size={32} className="text-purple-600" />
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900">Text to Video Generator</h1>
                    <p className="text-gray-600 mt-1">Generate short-form videos from text descriptions using Veo 3.1 AI</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Grid */}
            <div className="video-generation-content">

              {/* Input Section */}
              <div className="input-section">
                <div className="form-card">
                  <h2 className="section-title">Video Description</h2>
                  <div className="form-group">
                    <label htmlFor="video-prompt">Describe the video you want to generate</label>
                    <textarea
                      id="video-prompt"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Example: A serene sunrise over a mountain lake, with birds flying across the sky and gentle ripples on the water. Warm golden light illuminates the peaks."
                      rows="6"
                      className="prompt-textarea"
                      disabled={generating}
                    />
                    <p className="input-hint">
                      Be specific about scenes, actions, lighting, camera angles, and mood for best results.
                    </p>
                  </div>

                  <div className="config-row">
                    <div className="form-group">
                      <label htmlFor="duration">Duration</label>
                      <select
                        id="duration"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        className="config-select"
                        disabled={generating}
                      >
                        <option value="4">4 seconds</option>
                        <option value="6">6 seconds</option>
                        <option value="8">8 seconds</option>
                      </select>
                      <p className="input-hint" style={{marginTop: '4px', fontSize: '12px'}}>
                        Veo 3.1 supports videos up to 8 seconds
                      </p>
                    </div>

                    <div className="form-group">
                      <label htmlFor="quality">Quality</label>
                      <select
                        id="quality"
                        value={quality}
                        onChange={(e) => setQuality(e.target.value)}
                        className="config-select"
                        disabled={generating}
                      >
                        <option value="720p">720p HD</option>
                        <option value="1080p">1080p Full HD (8s only)</option>
                        <option value="4k">4K Ultra HD (8s only)</option>
                      </select>
                      <p className="input-hint" style={{marginTop: '4px', fontSize: '12px'}}>
                        1080p and 4K only available for 8-second videos
                      </p>
                    </div>
                  </div>

                  <div className="action-buttons">
                    <button
                      onClick={handleGenerateVideo}
                      disabled={generating || !prompt.trim()}
                      className="btn-primary"
                    >
                      {generating ? (
                        <>
                          <span className="spinner-small"></span>
                          Generating Video...
                        </>
                      ) : (
                        <>
                          <FiVideo size={18} />
                          Generate Video
                        </>
                      )}
                    </button>

                    {generatedVideo && (
                      <button
                        onClick={handleReset}
                        className="btn-secondary"
                      >
                        Generate New Video
                      </button>
                    )}
                  </div>

                  {error && (
                    <div className="error-message">
                      <strong>Error:</strong> {error}
                    </div>
                  )}
                </div>
              </div>

              {/* Results Section */}
              {generatedVideo && (
                <div className="results-section">
                  <div className="video-card">
                    <div className="video-card-header">
                      <h2 className="section-title">Generated Video</h2>
                      <button
                        onClick={handleDownloadVideo}
                        className="btn-download"
                        title="Download Video"
                      >
                        <FiDownload size={18} />
                        Download MP4
                      </button>
                    </div>

                    <div className="video-player-container">
                      <video
                        controls
                        preload="metadata"
                        className="video-player"
                        key={generatedVideo.url}
                      >
                        <source src={generatedVideo.url} type="video/mp4" />
                        Your browser doesn't support HTML5 video.
                        <a href={generatedVideo.url}>Download the video</a> instead.
                      </video>
                    </div>

                    <div className="video-metadata">
                      <div className="metadata-row">
                        <span className="metadata-label">Prompt:</span>
                        <span className="metadata-value">{generatedVideo.prompt}</span>
                      </div>
                      {generatedVideo.metadata && (
                        <>
                          <div className="metadata-row">
                            <span className="metadata-label">Resolution:</span>
                            <span className="metadata-value">
                              {generatedVideo.metadata.width} × {generatedVideo.metadata.height}
                            </span>
                          </div>
                          <div className="metadata-row">
                            <span className="metadata-label">Duration:</span>
                            <span className="metadata-value">
                              {generatedVideo.metadata.duration}s
                            </span>
                          </div>
                          <div className="metadata-row">
                            <span className="metadata-label">Size:</span>
                            <span className="metadata-value">
                              {(generatedVideo.metadata.size / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                        </>
                      )}
                      {tokenUsage && (
                        <div className="metadata-row">
                          <span className="metadata-label">Tokens Used:</span>
                          <span className="metadata-value">
                            {tokenUsage.totalTokens} total
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {generating && (
                <div className="results-section">
                  <div className="generating-state">
                    <div className="spinner-large"></div>
                    <h3>Generating your video with Veo 3.1...</h3>
                    <p>This may take 1-2 minutes. Please wait while we create your video.</p>
                    <p style={{fontSize: '13px', color: '#9ca3af', marginTop: '8px'}}>
                      Veo is generating high-quality video with native audio
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default VideoGeneration;
